// Which asset URLs a content doc actually references. Extracted from useAssetPreloader so there is ONE
// answer to that question.
//
// Two callers need it and they must never disagree: the preloader (what to fetch) and the live-update
// cache eviction (what to KEEP). If eviction's idea of "still referenced" drifted from the preloader's,
// it would dispose geometry or textures still on screen — a blank/black mesh with no error anywhere.
// One walk, two consumers.

import { filestore, FsImage, FsImage360, FsModel3D, FsVideo } from '@/filestore';
import { getFaceTrackingComponentIdsBySceneId } from '@r3f/utils/face-tracking';
import { isAbstractComponent } from '@r3f/utils/general';
import {
    FONT_TYPES,
    IComponentType,
    IComponentUnion,
    IContent360Scene,
    IRoot,
    ISceneComp,
    ITrackingTypes
} from '@r3f-component-data-structure';

export interface ICollectAssetUrlsInput {
    componentsById: { [id: string]: IComponentUnion };
    rootId: string;
}

/**
 * One troika `preloadFont` call. `characters` and `sdfGlyphSize` come from the FIRST component found
 * using this font, which is exactly what the pre-extraction code did — the URL alone is not enough to
 * reproduce the same preload.
 */
export interface IFontPreload {
    url: string;
    /** Non-optional: troika's preloadFont requires both, and the component fields they come from are too. */
    characters: string[];
    sdfGlyphSize: number;
}

export interface IAssetUrls {
    /** Texture URLs — images and 360 images. Deduplicated, in walk order. */
    imgUrls: string[];
    /** GLB URLs. Deduplicated, in walk order. */
    model3dUrls: string[];
    /** One entry per unique font. */
    fontPreloads: IFontPreload[];
}

/**
 * Resolve one asset URL, or return undefined.
 *
 * NEVER allowed to throw, and that contract is load-bearing on two separate paths.
 *
 * On the BOOT path (via useAssetPreloader) a single unresolvable reference used to reject
 * `loadContent()`, which meant `Jute.loaded()` never fired and the launch screen's spinner span forever
 * with no visible cause.
 *
 * On the RENDER paths it is worse: every one of them resolves above the `EntityErrorBoundary` it
 * renders — and `getPlacementSceneContent` builds unwrapped components inside a `useMemo` with no
 * per-entity boundary at all — so a throw escapes to the canvas-wide boundary in `ZapparCanvas`, which
 * unmounts the camera along with everything else and strands the launch screen. A missing asset should
 * cost that one asset, not the whole experience.
 *
 * It happens for real: `filestore.get` throws on an undefined id and returns `undefined` for an id
 * that was never registered — and a component can reference an id that is absent from
 * `mediaFilesById` (mid-edit, or after a media entry is removed while a component still points at it).
 */
export const resolveAssetUrl = (
    label: string,
    id: string | undefined,
    get: (fsId: string) => string
): string | undefined => {
    if (typeof id !== 'string' || !id) {
        console.warn(`[asset-urls] ${label} has no filestoreId — skipped`);
        return undefined;
    }
    try {
        const url = get(id);
        if (!url) throw new Error('resolved to an empty url');
        return url;
    } catch (err) {
        console.warn(`[asset-urls] ${label} could not resolve filestore id ${id} — skipped`, err);
        return undefined;
    }
};

/**
 * Walk every scene's entities and collect the URLs their assets resolve to.
 *
 * Resolution goes through `filestore`, so an id must already be registered (via `load`/`merge`) before
 * this runs — otherwise `filestore.get` throws for an unknown id.
 */
export function collectAssetUrls({ componentsById, rootId }: ICollectAssetUrlsInput): IAssetUrls {
    const imgUrls: string[] = [];
    const model3dUrls: string[] = [];
    const fontPreloads: IFontPreload[] = [];

    const resolve = resolveAssetUrl;
    const push = (into: string[], url: string | undefined) => {
        if (url && !into.includes(url)) into.push(url);
    };

    const root = componentsById[rootId] as IRoot | undefined;
    const sceneIds = root?.children ?? [];

    for (let i = 0; i < sceneIds.length; i++) {
        const sceneId = sceneIds[i];
        const scene = componentsById[sceneId] as ISceneComp | undefined;
        if (!scene) continue;

        const entityIds =
            scene.trackingType === ITrackingTypes.face
                ? getFaceTrackingComponentIdsBySceneId(sceneId, componentsById)
                : scene.children.filter((componentId) => !isAbstractComponent(componentsById[componentId]));

        if (!entityIds || entityIds.length === 0) continue;

        for (let j = 0; j < entityIds.length; j++) {
            const component = componentsById[entityIds[j]];
            if (!component) continue;

            switch (component.type) {
                case IComponentType.Scene: {
                    const { filestoreId, type } = (component as IContent360Scene).content360 ?? {};
                    // Only a 360-IMAGE scene contributes a texture; anything else here has no asset, so
                    // don't warn about a missing id.
                    if (type === 'image') {
                        push(imgUrls, resolve('360 scene', filestoreId, (id) => filestore.get<FsImage360>(id).getImageUrl()));
                    }
                    break;
                }
                case IComponentType.Model3d: {
                    push(model3dUrls, resolve('model', component.filestoreId, (id) => filestore.get<FsModel3D>(id).getModelUrl()));
                    break;
                }
                case IComponentType.Image: {
                    push(imgUrls, resolve('image', component.filestoreId, (id) => filestore.get<FsImage>(id).getImageUrl()));
                    break;
                }
                case IComponentType.Video: {
                    // Video.tsx loads its POSTER through the same useTextureLoader path as an Image, so the
                    // thumbnail lands in the URL-keyed texture cache. It must therefore appear in the
                    // keep-set, or eviction would dispose the poster of a video that is still on screen.
                    // (The video stream itself is cached separately, by entity id, and is not evictable
                    // here.)
                    push(imgUrls, resolve('video poster', component.filestoreId, (id) => filestore.get<FsVideo>(id).getThumbnailUrl()));
                    break;
                }
                case IComponentType.Text:
                case IComponentType.Text3d:
                case IComponentType.Button: {
                    const { url } = FONT_TYPES.filter((font) => font.fontFamily === component.fontFamily)[0] || FONT_TYPES[0];
                    if (!fontPreloads.some((font) => font.url === url)) {
                        // First component using this font wins, matching the pre-extraction behaviour.
                        fontPreloads.push({ url, characters: component.text.split(''), sdfGlyphSize: component.fontSize });
                    }
                    break;
                }
                default:
                    break;
            }
        }
    }

    return { imgUrls, model3dUrls, fontPreloads };
}
